/* Кадр «до / после» листа шаблона — исходный ползунок прототипа (baHTML, baSet в bot-studio.js).
   Двигается только transform: слой «после» снизу, слой «до» сдвигается на --p, его содержимое едет навстречу
   и стоит на месте, ручка .hd с кружком .kn идёт по линии раздела. Правила — bot-studio.css (.pre-ba).
   .nudge — подсказка при открытии, .glide — плавный переход по кнопкам «До» и «Результат». */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { uiText } from '../../i18n';
import { StudioIcon } from './StudioIcon';
import { studioMediaUrl } from './studio-preview-model';

export function StudioPreviewFrame({
  after,
  before,
  beforeLabel,
  afterLabel,
  label,
}: {
  after: ReactNode;
  before?: ReactNode;
  beforeLabel: string;
  afterLabel: string;
  label: string;
}) {
  const [position, setPosition] = useState(50);
  const [glide, setGlide] = useState(false);
  const [nudged, setNudged] = useState(false);
  const frame = useRef<HTMLDivElement>(null);
  /* жест на кадре, как BA шаблона: указатель, прямоугольник кадра, начало, уже тянут */
  const gesture = useRef<{ id: number; box: DOMRect; x0: number; drag: boolean } | null>(null);
  /* подсказка проигрывается один раз (анимация конечная); первая же установка раздела её снимает */
  useEffect(() => {
    const timer = setTimeout(() => setNudged(true), 2600);
    return () => clearTimeout(timer);
  }, []);
  if (!before)
    return (
      <div className="pre-st">
        <div className="pre-ba" style={{ ['--p' as string]: 100 }}>
          <span className="af">{after}</span>
        </div>
      </div>
    );
  /** Раздел на value (0 — видно только «после», 100 — только «до»); glide — доехать плавно (baSet шаблона) */
  const set = (value: number, glideTo: boolean) => {
    setNudged(true);
    setGlide(glideTo);
    setPosition(Math.max(0, Math.min(100, Math.round(value))));
  };
  const at = (clientX: number, box: DOMRect) => ((clientX - box.left) / box.width) * 100;
  const jump = (value: number) => set(value, true);
  return (
    <>
      <div className="pre-st">
        <div
          ref={frame}
          className={`pre-ba${nudged ? '' : ' nudge'}${glide ? ' glide' : ''}`}
          style={{ ['--p' as string]: position }}
          role="slider"
          tabIndex={0}
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={position}
          aria-valuetext={position >= 100 ? beforeLabel : position <= 0 ? afterLabel : uiText('Поровну')}
          /* Тянуть — раздел идёт за пальцем после сдвига больше 4 px по горизонтали (вертикальный жест остаётся прокрутке листа,
             touch-action: pan-y); нажать — раздел доезжает до точки (bot-studio.js, «До / после») */
          onPointerDown={event => {
            if (event.button > 0) return;
            const box = event.currentTarget.getBoundingClientRect();
            if (!box.width) return;
            gesture.current = { id: event.pointerId, box, x0: event.clientX, drag: false };
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              /* без захвата жест всё равно доводится по pointerup */
            }
          }}
          onPointerMove={event => {
            const g = gesture.current;
            if (!g || event.pointerId !== g.id) return;
            if (!g.drag) {
              if (Math.abs(event.clientX - g.x0) < 4) return;
              g.drag = true;
            }
            set(at(event.clientX, g.box), false);
          }}
          onPointerUp={event => {
            const g = gesture.current;
            if (!g || event.pointerId !== g.id) return;
            if (!g.drag) set(at(event.clientX, g.box), true);
            gesture.current = null;
          }}
          onPointerCancel={() => {
            gesture.current = null;
          }}
          onKeyDown={event => {
            const step =
              event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -10 : event.key === 'ArrowRight' || event.key === 'ArrowUp' ? 10 : 0;
            if (!step && event.key !== 'Home' && event.key !== 'End') return;
            event.preventDefault();
            jump(event.key === 'Home' ? 0 : event.key === 'End' ? 100 : Math.max(0, Math.min(100, position + step)));
          }}
        >
          <span className="af">{after}</span>
          <span className="bf">
            <span className="in">{before}</span>
          </span>
          <span className="hd" aria-hidden="true">
            <i className="kn">
              <StudioIcon kind="back" />
              <StudioIcon kind="chev" />
            </i>
          </span>
        </div>
      </div>
      <div className="pre-bt">
        <button type="button" className={position >= 100 ? 'on' : undefined} aria-pressed={position >= 100} onClick={() => jump(100)}>
          {beforeLabel}
        </button>
        <span className="ar" aria-hidden="true">
          <StudioIcon kind="arrow" />
        </span>
        <button type="button" className={position <= 0 ? 'on' : undefined} aria-pressed={position <= 0} onClick={() => jump(0)}>
          {afterLabel}
        </button>
      </div>
    </>
  );
}

/** Аура листа: обложка шаблона, сильно размытая (preAura прототипа: фильтр — прямо в style картинки, как у cover()).
    Сдвиг кадра (object-position: 50% pos%) прототип берёт из записи шаблона (a.pos), в документе §6.3 такого поля нет — кадр по центру.
    Адрес — уже проверенный safeMediaUrl (studioMedia) */
export function StudioPreviewAura({ cover }: { cover?: string }) {
  const url = studioMediaUrl(cover);
  if (!url) return null;
  return (
    <div className="pre-au" aria-hidden="true">
      <img src={url} alt="" style={{ filter: 'blur(46px) saturate(1.5)' }} draggable={false} referrerPolicy="no-referrer" />
    </div>
  );
}
